import type { CalendarAppointment } from '@cartwheel/api/v1'
import { AppointmentPill } from './appointment-pill'
import { DAYS_OF_WEEK } from './utils'

interface MonthViewProps {
  year: number
  month: number
  now: Date
  appointmentsByDate: Record<string, CalendarAppointment[]>
  expandedDays: Set<string>
  onExpandDay: (dateStr: string) => void
  onAppointmentClick: (appointment: CalendarAppointment) => void
}

export function MonthView({
  year,
  month,
  now,
  appointmentsByDate,
  expandedDays,
  onExpandDay,
  onAppointmentClick,
}: MonthViewProps) {
  const firstDay = new Date(year, month - 1, 1).getDay()
  const daysInMonth = new Date(year, month, 0).getDate()

  return (
    <>
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-l border-t border-gray-stroke">
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            className="border-r border-b border-gray-stroke text-center text-sm font-medium text-gray-500 py-2 bg-gray-50"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 border-l border-gray-stroke">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static leading cells with no meaningful key
            key={`empty-${i}`}
            className="border-r border-b border-gray-stroke min-h-20 bg-gray-50"
          />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          const dayAppointments = appointmentsByDate[dateStr] ?? []
          const isToday =
            day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear()

          return (
            <div
              key={dateStr}
              className="border-r border-b border-gray-stroke min-h-20 p-1 flex flex-col gap-0.5"
            >
              <span
                className={`text-xs leading-tight mb-0.5 w-5 h-5 flex items-center justify-center rounded-full ${
                  isToday ? 'bg-forest text-white font-semibold' : 'text-gray-600'
                }`}
              >
                {day}
              </span>
              {(expandedDays.has(dateStr) ? dayAppointments : dayAppointments.slice(0, 3)).map(
                (appt) => (
                  <AppointmentPill key={appt.id} appointment={appt} onClick={onAppointmentClick} />
                )
              )}
              {dayAppointments.length > 3 && !expandedDays.has(dateStr) && (
                <button
                  type="button"
                  onClick={() => onExpandDay(dateStr)}
                  className="w-full rounded px-2 py-1 text-xs bg-forest-dark text-white text-left hover:bg-forest cursor-pointer"
                >
                  +{dayAppointments.length - 3} more
                </button>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
